import React, { useEffect, useState } from "react";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import styled from "@emotion/styled";
import { useFirestore } from "../firebase/FirestoreContext";
import { collectionPath } from "../context/EnvContext";

const Container = styled(Box)`
  margin-top: 16px;
  margin-bottom: 16px;
`;

const OptionContainer = styled(FormControlLabel)`
  width: 100%;
  margin: 0;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-bottom: 6px;
  gap: 12px;

  &:hover {
    background-color: #f8f9fa;
  }

  .MuiFormControlLabel-label {
    width: 100%;
  }

  .MuiRadio-root {
    padding: 0;
  }
`;

const OptionContent = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 12px;
`;

const OptionDetails = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const OptionName = styled(Typography)`
  font-weight: 500;
  font-size: 15px;
  color: #333;
  line-height: 1.3;
`;

const DeliveryTime = styled(Typography)`
  font-size: 12px;
  color: #666;
  margin-top: 1px;
  line-height: 1.2;
`;

const Price = styled(Typography)`
  font-size: 15px;
  font-weight: 600;
  color: ${(props) => (props.free ? "#00a000" : "#333")};
  white-space: nowrap;
`;

const ShippingOptions = ({ cartPrice, selectedSlug, onChange }) => {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(selectedSlug || "");
  const [loading, setLoading] = useState(true);
  const db = useFirestore();

  useEffect(() => {
    const fetchShippingOptions = async () => {
      try {
        console.log(
          `${collectionPath}ShippingOptions/ShippingOptionsList/DataBase`,
        );
        const shippingCollection = collection(
          db,
          `${collectionPath}ShippingOptions/ShippingOptionsList/DataBase`,
        );
        const q = query(shippingCollection, orderBy("price", "asc"));
        const snapshot = await getDocs(q);

        const fetchedOptions = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setOptions(fetchedOptions);

        // If no selection yet, select the best default option
        if (!selectedSlug && fetchedOptions.length > 0) {
          const bestOption = findBestOption(fetchedOptions, cartPrice);
          setSelected(bestOption.slug);
          const isFreeShipping =
            bestOption.free_threshold > 0 &&
            cartPrice >= bestOption.free_threshold;
          onChange({
            shippingCost: isFreeShipping ? 0 : bestOption.price,
            shippingOption: bestOption,
            isFreeShipping,
          });
        }
      } catch (error) {
        console.error("Error fetching shipping options:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchShippingOptions();
  }, [db, cartPrice, selectedSlug, onChange]);

  const findBestOption = (options, cartPrice) => {
    // Find the fastest free option if available
    const freeOptions = options.filter(
      (opt) => opt.free_threshold > 0 && cartPrice >= opt.free_threshold,
    );

    if (freeOptions.length > 0) {
      // Return the free option with shortest delivery time (lowest maximum_days)
      return freeOptions.reduce((best, current) =>
        current.maximum_days < best.maximum_days ? current : best,
      );
    }

    // Otherwise return the first option (cheapest since they're sorted by price)
    return options[0];
  };

  const handleChange = (event) => {
    const selectedSlug = event.target.value;
    setSelected(selectedSlug);
    const selectedOption = options.find((opt) => opt.slug === selectedSlug);
    if (selectedOption && onChange) {
      const isFreeShipping =
        selectedOption.free_threshold > 0 &&
        cartPrice >= selectedOption.free_threshold;
      onChange({
        shippingCost: isFreeShipping ? 0 : selectedOption.price,
        shippingOption: selectedOption,
        isFreeShipping,
      });
    }
  };

  const formatDeliveryTime = (minDays, maxDays) => {
    if (minDays === maxDays) {
      return `${minDays} business day${minDays > 1 ? "s" : ""}`;
    }
    return `${minDays}-${maxDays} business days`;
  };

  const getShippingPrice = (option) => {
    if (option.free_threshold > 0 && cartPrice >= option.free_threshold) {
      return { text: "Free", isFree: true };
    }
    return { text: `$${option.price.toFixed(2)}`, isFree: false };
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={24} />
        </Box>
      </Container>
    );
  }

  if (options.length === 0) {
    return (
      <Container>
        <Typography color="error">No shipping options available</Typography>
      </Container>
    );
  }

  return (
    <Container>
      <FormControl component="fieldset" fullWidth>
        <RadioGroup value={selected} onChange={handleChange}>
          {options.map((option) => {
            const { text: priceText, isFree } = getShippingPrice(option);
            const deliveryTime = formatDeliveryTime(
              option.minimum_days,
              option.maximum_days,
            );

            return (
              <OptionContainer
                key={option.slug}
                value={option.slug}
                control={<Radio />}
                label={
                  <OptionContent>
                    <OptionDetails>
                      <OptionName>
                        <strong>{option.name}</strong> ({deliveryTime})
                      </OptionName>
                      {option.free_threshold > 0 && (
                        <DeliveryTime>
                          Free on orders of ${option.free_threshold.toFixed(0)}+
                        </DeliveryTime>
                      )}
                    </OptionDetails>
                    <Price free={isFree}>{priceText}</Price>
                  </OptionContent>
                }
              />
            );
          })}
        </RadioGroup>
      </FormControl>
    </Container>
  );
};

export default ShippingOptions;
